import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Calendar, Clock, MapPin } from "lucide-react";

const empty = {
    course_id: "",
    title: "",
    start_at: "",
    end_at: "",
    mode: "offline",
    instructor: "",
    location_or_link: "",
    notes: "",
    status: "scheduled",
};

function fmtDT(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch (e) { return iso; }
}

function toInputDT(iso) {
    if (!iso) return "";
    // datetime-local needs "YYYY-MM-DDTHH:MM"
    try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch (e) { return ""; }
}

function fromInputDT(s) {
    if (!s) return "";
    // Convert local datetime-local string to ISO with offset
    try { return new Date(s).toISOString(); } catch (e) { return s; }
}

function SessionEditor({ open, onOpenChange, initial, courses, onSaved }) {
    const isEdit = Boolean(initial?.id);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            const seed = initial ? {
                ...empty, ...initial,
                start_at: toInputDT(initial.start_at),
                end_at: toInputDT(initial.end_at),
            } : empty;
            setForm(seed);
        }
    }, [open, initial]);

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.course_id) { toast.error("Pick a course"); return; }
        setSaving(true);
        try {
            const payload = {
                ...form,
                start_at: fromInputDT(form.start_at),
                end_at: fromInputDT(form.end_at),
            };
            if (isEdit) await api.patch(`/admin/class-sessions/${initial.id}`, payload);
            else await api.post("/admin/class-sessions", payload);
            toast.success(isEdit ? "Session updated" : "Session added");
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="session-editor-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">{isEdit ? "Edit session" : "New class session"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={save} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Course</Label>
                            <Select value={form.course_id} onValueChange={(v) => setForm((f) => ({ ...f, course_id: v }))}>
                                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="session-form-course">
                                    <SelectValue placeholder="Pick a course" />
                                </SelectTrigger>
                                <SelectContent>
                                    {courses.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Title</Label>
                            <Input name="title" value={form.title} onChange={onChange} required placeholder="e.g. Week 2 — Sun Salutations" className="mt-1.5 rounded-xl" data-testid="session-form-title" />
                        </div>
                        <div>
                            <Label>Starts at</Label>
                            <Input type="datetime-local" name="start_at" value={form.start_at} onChange={onChange} required className="mt-1.5 rounded-xl" data-testid="session-form-start" />
                        </div>
                        <div>
                            <Label>Ends at</Label>
                            <Input type="datetime-local" name="end_at" value={form.end_at} onChange={onChange} required className="mt-1.5 rounded-xl" data-testid="session-form-end" />
                        </div>
                        <div>
                            <Label>Mode</Label>
                            <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
                                <SelectTrigger className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="offline">Offline (studio)</SelectItem>
                                    <SelectItem value="online">Online (Zoom/Meet)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Status</Label>
                            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                                <SelectTrigger className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="scheduled">Scheduled</SelectItem>
                                    <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="sm:col-span-2">
                            <Label>Instructor</Label>
                            <Input name="instructor" value={form.instructor} onChange={onChange} placeholder="Aisha M." className="mt-1.5 rounded-xl" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label>{form.mode === "online" ? "Meeting link" : "Location"}</Label>
                            <Input name="location_or_link" value={form.location_or_link} onChange={onChange} placeholder={form.mode === "online" ? "https://meet.google.com/…" : "Studio A, 2nd floor"} className="mt-1.5 rounded-xl" />
                        </div>
                    </div>
                    <div>
                        <Label>Notes (optional)</Label>
                        <Textarea name="notes" value={form.notes} onChange={onChange} rows={2} className="mt-1.5 rounded-xl" />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                        <Button type="submit" disabled={saving} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="session-form-save">
                            {saving ? "Saving…" : isEdit ? "Save" : "Add session"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function SchedulingTab() {
    const [sessions, setSessions] = useState([]);
    const [courses, setCourses] = useState([]);
    const [filter, setFilter] = useState("all");
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const reload = () => {
        const params = filter && filter !== "all" ? { course_id: filter } : {};
        api.get("/admin/class-sessions", { params }).then(({ data }) => setSessions(data));
    };
    useEffect(() => {
        api.get("/admin/courses").then(({ data }) => setCourses(data));
    }, []);
    useEffect(() => { reload(); }, [filter]); // eslint-disable-line

    const grouped = useMemo(() => {
        const byCourse = new Map();
        for (const s of sessions) {
            if (!byCourse.has(s.course_id)) byCourse.set(s.course_id, []);
            byCourse.get(s.course_id).push(s);
        }
        return Array.from(byCourse.entries()).map(([id, list]) => ({ course: courses.find((c) => c.id === id), id, list }));
    }, [sessions, courses]);

    const remove = async (id) => {
        if (!confirm("Delete this session?")) return;
        try {
            await api.delete(`/admin/class-sessions/${id}`);
            toast.success("Session deleted");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="space-y-4" data-testid="scheduling-tab">
            <div className="flex flex-wrap justify-between items-end gap-3">
                <div>
                    <h3 className="font-heading text-2xl flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-brand-600" /> Class schedule
                    </h3>
                    <p className="text-sm text-ink-700">Add session rows per course. Enrolled users see them on their account page and the course detail page.</p>
                </div>
                <div className="flex gap-2">
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="w-60 rounded-full" data-testid="session-filter-course">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All courses</SelectItem>
                            {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="session-new-btn">
                        <Plus className="h-4 w-4 mr-1.5" /> Add session
                    </Button>
                </div>
            </div>

            {sessions.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center text-ink-700">
                    No sessions yet. Add one to fill the schedule.
                </div>
            ) : (
                <div className="space-y-6">
                    {grouped.map((g) => (
                        <div key={g.id}>
                            <h4 className="font-heading text-lg mb-2">{g.course?.title || g.id}</h4>
                            <div className="space-y-2">
                                {g.list.map((s) => (
                                    <div key={s.id} className="rounded-2xl bg-white border border-gray-100 p-4 flex flex-wrap justify-between items-start gap-3" data-testid={`session-row-${s.id}`}>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`text-[11px] uppercase tracking-wide rounded-full px-2 py-0.5 ${s.mode === "online" ? "bg-brand-50 text-brand-700" : "bg-accent-50 text-accent-700"}`}>{s.mode}</span>
                                                {s.status === "cancelled" && <span className="text-[11px] uppercase tracking-wide rounded-full px-2 py-0.5 bg-red-100 text-red-700">cancelled</span>}
                                                <span className="font-semibold">{s.title}</span>
                                            </div>
                                            <div className="text-xs text-ink-700 mt-1 flex flex-wrap gap-3">
                                                <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDT(s.start_at)} → {fmtDT(s.end_at)}</span>
                                                {s.instructor && <span>👤 {s.instructor}</span>}
                                                {s.location_or_link && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.location_or_link}</span>}
                                            </div>
                                        </div>
                                        <div className="flex gap-1.5">
                                            <Button size="icon" variant="outline" className="rounded-full h-8 w-8" onClick={() => { setEditing(s); setOpen(true); }} data-testid={`session-edit-${s.id}`}>
                                                <Pencil className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button size="icon" variant="outline" className="rounded-full h-8 w-8 text-accent-700 hover:bg-accent-50" onClick={() => remove(s.id)} data-testid={`session-delete-${s.id}`}>
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <SessionEditor open={open} onOpenChange={setOpen} initial={editing} courses={courses} onSaved={reload} />
        </div>
    );
}
